import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const inputVariants = cva(
  'flex w-full border border-input bg-background px-4 py-3 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-neutral-400 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 font-acuminPro uppercase transition-colors',
  {
    variants: {
      size: {
        default: 'h-10 text-sm lg:text-base',
        sm: 'h-8 text-sm',
        lg: 'h-12 text-lg',
      },
      hasError: {
        true: 'border-red-500 focus-visible:border-red-500',
        false: 'focus-visible:border-neutral-900',
      },
    },
    defaultVariants: {
      size: 'default',
      hasError: false,
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  errorMessage?: string;
  label?: string;
  required?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, errorMessage, hasError, label, required, ...props }, ref) => {
    return (
      <div className='relative w-full'>
        {label && (
          <label className='text-base font-semibold text-neutral-950 font-acuminPro uppercase' htmlFor={props.id}>
            {label}
            {required && <span className='text-hb-orange ml-1'>*</span>}
          </label>
        )}
        <input
          id={props.id}
          type={type}
          className={cn(
            inputVariants({
              size,
              hasError: hasError || !!errorMessage,
              className,
            }),
          )}
          ref={ref}
          {...props}
        />
        {errorMessage && <div className='absolute -bottom-5 left-0 text-red-500 text-sm'>{errorMessage}</div>}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
